export type TabularConfigTab =
  | 'tables'
  | 'checks'
  | 'description'
  | 'script'
  | 'fingerprint'
  | 'pages'
  | 'history'

export interface TabularConfigPathOptions {
  /** A saved version number; omitted for the latest. */
  version?: number
  tab?: TabularConfigTab
  /** History's *diff to previous*: the older version to compare `version` against. */
  compare?: number
}

/** An output row a run page should point at: its table and 0-based position among the CSV's data rows. */
export interface TabularRowRef {
  table: string
  position: number
}

export interface TabularJobView {
  /** `pages` or an output table's name. */
  tab?: string
  page?: string
  row?: TabularRowRef
}

export function tabularJobViewParams(view: TabularJobView): URLSearchParams {
  const params = new URLSearchParams()
  if (view.tab) params.set('tab', view.tab)
  if (view.page) params.set('page', view.page)
  if (view.row) params.set('row', `${view.row.table}:${view.row.position}`)
  return params
}

export function tabularJobPath(jobId: string, view: TabularJobView = {}): string {
  const query = tabularJobViewParams(view).toString()
  return `/ingest/jobs/${encodeURIComponent(jobId)}/tabular${query ? `?${query}` : ''}`
}

/** Reads the `row` a run path carries; `undefined` when absent or malformed. */
export function parseTabularRowRef(value: string | null): TabularRowRef | undefined {
  const match = value ? /^(.+):(\d+)$/.exec(value) : null
  if (!match?.[1] || !match[2]) return undefined
  return { table: match[1], position: Number(match[2]) }
}

/** One page of an agent run, full width; `filter` is the page list J / K steps through. */
export function tabularJobPagePath(jobId: string, pageId: string, filter?: 'todo'): string {
  const path = `/ingest/jobs/${encodeURIComponent(jobId)}/tabular/pages/${encodeURIComponent(pageId)}`
  return filter ? `${path}?filter=${filter}` : path
}

/** The config itself: saved versions, plus a pointer to any build in flight. */
export function tabularConfigPath(
  configName: string,
  versionOrOptions?: number | TabularConfigPathOptions
): string {
  const options: TabularConfigPathOptions =
    typeof versionOrOptions === 'number' ? { version: versionOrOptions } : (versionOrOptions ?? {})
  const params = new URLSearchParams()
  if (options.version !== undefined) params.set('version', String(options.version))
  if (options.compare !== undefined) params.set('compare', String(options.compare))
  if (options.tab) params.set('tab', options.tab)
  const query = params.toString()
  return `/ingest/configs/${encodeURIComponent(configName)}${query ? `?${query}` : ''}`
}

/**
 * One config build, keyed by its job id so the URL survives refresh, sharing,
 * and the build finishing — every build of a config gets its own page. This is
 * also the draft review: a build awaiting review renders as its draft.
 */
export function tabularConfigBuildPath(
  configName: string,
  buildJobId: string,
  tab?: TabularConfigTab
): string {
  const path = `/ingest/configs/${encodeURIComponent(configName)}/builds/${encodeURIComponent(buildJobId)}`
  return tab ? `${path}?tab=${tab}` : path
}

/** The builder's tool calls for one build, on their own page (debug mode only). */
export function tabularConfigBuildToolCallsPath(configName: string, buildJobId: string): string {
  return `${tabularConfigBuildPath(configName, buildJobId)}/tool-calls`
}
